
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Procurement } from '@/types/database.types';
import { supabase } from '@/integrations/supabase/client';

export const useUpdateProcurementStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updateData: { 
      id: string; 
      status?: string; 
      approved_by?: string; 
      rejected_by?: string; 
      rejection_source?: 'finance' | 'warehouse';
      finance_approved_at?: string; 
      finance_rejected_at?: string; 
      rejection_reason?: string; 
      [key: string]: any 
    }) => {
      const { id, approved_by, rejected_by, finance_approved_at, finance_rejected_at, rejection_reason, ...procurementFields } = updateData;
      console.log(`Updating procurement ${id} with status:`, procurementFields.status);
      console.log('Full update data:', procurementFields);
      
      // First check if the procurement exists
      const { data: existingProcurement, error: checkError } = await supabase
        .from('procurements')
        .select('id, status')
        .eq('id', id)
        .maybeSingle();

      if (checkError) {
        console.error(`Error checking procurement ${id}:`, checkError);
        throw checkError;
      }

      if (!existingProcurement) {
        throw new Error(`Procurement with ID ${id} not found`);
      }

      console.log(`Found existing procurement ${id} with current status:`, existingProcurement.status);

      // Update the procurement table - use update without select first
      const { error: updateError } = await supabase
        .from('procurements')
        .update(procurementFields)
        .eq('id', id);

      if (updateError) {
        console.error(`Error updating procurement ${id}:`, updateError);
        throw updateError;
      }

      console.log(`Successfully updated procurement ${id} with new status:`, procurementFields.status);

      // Then fetch the updated data separately to verify
      const { data: updatedData, error: fetchError } = await supabase
        .from('procurements')
        .select('id, status')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) {
        console.error(`Error fetching updated procurement ${id}:`, fetchError);
        // Don't throw here, the update might have succeeded
      }

      console.log(`Verified updated procurement ${id}. Current status:`, updatedData?.status);

      // Update procurement_finance table if rejection fields are provided
      // Note: Approval fields are now handled directly in ApproveConfirmationDialog
      if (rejected_by || finance_rejected_at || rejection_reason) {
        const financeUpdateData: any = {};
        if (rejected_by) financeUpdateData.rejected_by = rejected_by;
        if (finance_rejected_at) financeUpdateData.finance_rejected_at = finance_rejected_at;
        if (rejection_reason) financeUpdateData.rejection_reason = rejection_reason;

        console.log(`Updating procurement finance for ${id} with:`, financeUpdateData);

        const { error: financeError } = await supabase
          .from('procurement_finance')
          .update(financeUpdateData)
          .eq('procurement_id', id);

        if (financeError) {
          console.error(`Error updating procurement finance ${id}:`, financeError);
          throw financeError;
        }
      }

      // Return the updated procurement data
      return updatedData as Procurement;
    },
    onSuccess: (data, { id }) => {
      console.log(`Successfully updated procurement ${id} to status:`, data?.status);
      console.log(`Invalidating queries for procurement ${id}`);
      
      // Invalidate all procurement-related queries to force refetch
      queryClient.invalidateQueries({ queryKey: ['procurements'] });
      queryClient.invalidateQueries({ queryKey: ['procurement', id] });
      queryClient.invalidateQueries({ queryKey: ['procurements', 'current'] });
      
      // Force refetch after a small delay to ensure database has been updated
      setTimeout(() => {
        console.log('Force refetching procurement queries');
        queryClient.refetchQueries({ queryKey: ['procurements'] });
        queryClient.refetchQueries({ queryKey: ['procurement', id] });
      }, 500);
    },
    onError: (error, { id }) => {
      console.error(`Error updating procurement ${id}:`, error);
    }
  });
};
